// src/context/SubscriptionContext.js
import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../utils/supabase';
import {
  FREE_LIMITS, PRO_LIMITS, PRO_ONLY_FEATURES,
  getLimitsForTier, canTierAccessFeature, isTierAtLeast, TIER_ORDER,
} from '../config/pricing';

const SubscriptionContext = createContext({});

// Re-export from pricing config for backward compatibility
export { FREE_LIMITS, PRO_LIMITS, PRO_ONLY_FEATURES };

// Subscription statuses
export const SUBSCRIPTION_STATUS = {
  FREE: 'free',
  ACTIVE: 'active',
  LIFETIME: 'lifetime',
  CANCELED: 'canceled',
  PAST_DUE: 'past_due',
  PENDING: 'pending', // Checkout started but payment not completed
  NONE: 'none',
};

export const SubscriptionProvider = ({ children }) => {
  const { currentUser, loading: authLoading } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  // Use ref to track if initial load is done (refs persist across re-renders without causing re-renders)
  const initialLoadDoneRef = useRef(false);

  // Fetch subscription from database
  const fetchSubscription = useCallback(async () => {
    if (!currentUser?.uid) {
      setSubscription(null);
      setLoading(false);
      initialLoadDoneRef.current = true;
      return;
    }

    // Only show loading on initial load, not on refreshes (prevents flash when tabbing back)
    if (!initialLoadDoneRef.current) {
      setLoading(true);
    }

    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', currentUser.uid)
        .maybeSingle();

      // PGRST116 = no rows returned, 406 = RLS blocking empty result
      if (error && error.code !== 'PGRST116' && error.code !== '406') {
        console.error('Error fetching subscription:', error);
      }

      if (data) {
        // Only update if subscription data actually changed (prevents unnecessary re-renders)
        setSubscription(prev => {
          if (prev?.id === data.id &&
              prev?.status === data.status &&
              prev?.stripe_subscription_id === data.stripe_subscription_id &&
              prev?.payment_provider === data.payment_provider &&
              prev?.current_period_end === data.current_period_end &&
              prev?.cancel_at_period_end === data.cancel_at_period_end &&
              prev?.plan_type === data.plan_type &&
              prev?.billing_interval === data.billing_interval) {
            return prev; // Keep same reference
          }
          return data;
        });
      } else {
        // No subscription record - user is on free plan
        setSubscription(prev => {
          if (prev?.status === SUBSCRIPTION_STATUS.FREE && !prev?.id) {
            return prev; // Keep same reference for free status
          }
          return {
            status: SUBSCRIPTION_STATUS.FREE,
            plan_type: 'free',
          };
        });
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setLoading(false);
      initialLoadDoneRef.current = true;
    }
  }, [currentUser]);

  // Fetch subscription when user changes
  useEffect(() => {
    if (!authLoading && currentUser?.uid) {
      fetchSubscription();
    }
    // Only depend on uid to prevent re-fetches on object reference changes
  }, [currentUser?.uid, authLoading]); // eslint-disable-line

  // Subscribe to subscription changes
  useEffect(() => {
    if (!currentUser?.uid) return;

    const channel = supabase
      .channel(`subscription-${currentUser.uid}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'subscriptions',
          filter: `user_id=eq.${currentUser.uid}`,
        },
        (payload) => {
          if (payload.new) {
            setSubscription(payload.new);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentUser?.uid]);

  // Get subscription status details - memoized to prevent unnecessary re-renders.
  //
  // 2026-05-30 cleanup: dropped isStarterOrHigher / isEnterprise from the
  // returned object. They were 4-tier-era aliases that all collapsed to
  // isPro in the 2-tier world. All call sites have been migrated to
  // check isPro directly (see Phase 4 commit). Anyone destructuring
  // them now gets `undefined` — a hard fail at runtime that surfaces
  // any stragglers loudly, instead of silently treating paid users as
  // free.
  const subscriptionStatus = useMemo(() => {
    if (!subscription) {
      return {
        status: SUBSCRIPTION_STATUS.NONE,
        currentTier: 'free',
        billingInterval: null,
        isActive: false,
        isFree: true,
        isLifetime: false,
        isCanceled: false,
        isPastDue: false,
        isPro: false,
        isProOrHigher: false,
        hasPaidSubscription: false,
        hasLimits: true,
        needsUpgrade: true,
        needsResubscribe: false,
      };
    }

    const status = subscription.status;
    const isFree = status === SUBSCRIPTION_STATUS.FREE || status === SUBSCRIPTION_STATUS.PENDING || !status;
    const isActive = status === SUBSCRIPTION_STATUS.ACTIVE;
    // Detect lifetime: explicit status, plan_type, or subscription ID starts with "lifetime_"
    const isLifetime = status === SUBSCRIPTION_STATUS.LIFETIME ||
                       subscription.plan_type === 'lifetime' ||
                       (subscription.stripe_subscription_id?.startsWith('lifetime_'));
    const isCanceled = status === SUBSCRIPTION_STATUS.CANCELED;
    const isPastDue = status === SUBSCRIPTION_STATUS.PAST_DUE;

    // ── Tier resolution ─────────────────────────────────────────────
    // Determine the current tier from plan_type column. In the 2-tier
    // world (post-migration 20260601), legacy plan_type values
    // ('starter', 'enterprise', 'lifetime') normalize down to 'pro'
    // via the helpers in pricing.js — but we still preserve the raw
    // legacy values here in case anyone wants to inspect the original
    // subscription record.
    let currentTier = subscription.plan_type || 'free';
    if (!subscription.plan_type && isActive) {
      currentTier = 'pro'; // Legacy active subs without plan_type are Pro/Plus
    }
    if (isLifetime) {
      // Lifetime users get full Plus-level access.
      const ltPlan = subscription.plan_type;
      currentTier = (ltPlan && ltPlan !== 'lifetime') ? ltPlan : 'pro';
    }
    if (isFree) {
      currentTier = 'free';
    }

    // No trial state — we decided 2026-05-30 not to ship trials. Trial-
    // period-days flows are too easy to abuse at the $10 price point
    // (multi-card / throwaway-email attacks), and the Free tier is
    // generous enough to serve as the funnel. Users upgrade directly
    // to Plus.

    // ── Tier hierarchy checks (paid users only) ────────────────────
    // In the 2-tier world all paid users have the same access, so
    // isPro and isProOrHigher are synonymous. Kept isProOrHigher as
    // an alias for the few read-only "is this user paid?" call sites
    // that read more naturally with the "OrHigher" suffix (e.g. when
    // referenced in feature-gating logic that historically asked the
    // question "are you at least Pro?").
    //
    // isStarterOrHigher / isEnterprise were dropped in Phase 4 cleanup —
    // see comment on the empty-subscription branch above.
    const hasPaidStatus = isActive || isLifetime;
    const isPro = hasPaidStatus;
    const isProOrHigher = hasPaidStatus;

    // ── Other derived state ─────────────────────────────────────────
    const hasPaidSubscription = !!subscription.stripe_subscription_id || isLifetime;

    // User needs to upgrade if they're on free plan
    const needsUpgrade = isFree;

    // Block access and show resubscribe page for canceled or past_due
    // subscriptions. Lifetime users are never blocked.
    const needsResubscribe = !isLifetime && (isCanceled || isPastDue);

    // Free has count-based limits, Plus is unlimited everywhere. Lifetime
    // users have NO limits on anything (including weekly tracking).
    const tierLimits = getLimitsForTier(currentTier);
    const hasLimits = isLifetime ? false : (tierLimits.MAX_INVENTORY_ITEMS !== null || tierLimits.MAX_SALES !== null);

    return {
      status: isFree ? SUBSCRIPTION_STATUS.FREE : status,
      currentTier,
      billingInterval: subscription.billing_interval || null,
      isActive,
      isFree,
      isLifetime,
      isCanceled,
      isPastDue,
      // Tier hierarchy. In the 2-tier world isPro and isProOrHigher
      // are synonymous; both are kept so call sites read naturally.
      // isStarterOrHigher / isEnterprise were removed (see comment
      // on the no-subscription early return above).
      isPro,
      isProOrHigher,
      hasPaidSubscription,
      hasLimits,
      needsUpgrade,
      needsResubscribe,
      currentPeriodEnd: subscription.current_period_end,
      cancelAtPeriodEnd: subscription.cancel_at_period_end,
      paymentProvider: subscription.payment_provider || 'stripe',
    };
  }, [subscription]);

  // Refresh subscription data (silent refresh, no loading state)
  const refreshSubscription = useCallback(async () => {
    await fetchSubscription();
  }, [fetchSubscription]);

  // Check if user is within limits for a specific feature (tier-aware).
  //
  // Renamed from checkFreeLimit -> checkUsageLimit on 2026-05-30 (Phase 4B).
  // The original name was misleading: the function always checked the
  // user's CURRENT tier's limits, not specifically "free" limits. Plus
  // users hit it constantly too — they just get back null limits.
  // checkTrialLimit was an even older alias from before trials were
  // dropped. Both have been removed; all call sites now use the new name.
  const checkUsageLimit = useCallback((feature, currentCount) => {
    // Lifetime users have NO limits on anything
    if (subscriptionStatus.isLifetime) {
      return { allowed: true, limit: null, remaining: null };
    }
    const tierLimits = getLimitsForTier(subscriptionStatus.currentTier);

    // Map feature names to limit keys. Per 2026-05-30 decisions:
    //   - 'scraper_orders' has no entry (scraped orders flow into the
    //     unified Orders & Tracking page; the combined quota below is
    //     what enforces Free-tier limits there)
    //   - 'orders_tracking' is the combined weekly quota for the unified
    //     Orders & Tracking page (scraped orders + tracked packages per
    //     ISO week, Free=25, Plus=unlimited). The `currentCount` passed
    //     in must be the COMBINED count — fetched via
    //     useWeeklyOrdersTrackingUsage / get_combined_weekly_usage RPC.
    //   - 'tracking' / 'packages' have no entry (the old per-API weekly
    //     refresh quota is retired; will be reintroduced when the new
    //     tracking API architecture ships)
    // Any call site passing a feature not in this map falls through to
    // the "allowed, unlimited" branch below.
    const limitKeyMap = {
      inventory: 'MAX_INVENTORY_ITEMS',
      sales: 'MAX_SALES',
      expenses: 'MAX_EXPENSES',
      vault: 'MAX_VAULT_ITEMS',
      orders_tracking: 'MAX_WEEKLY_ORDERS_TRACKING',
    };

    const limitKey = feature in limitKeyMap ? limitKeyMap[feature] : null;
    const limit = limitKey ? tierLimits[limitKey] : null;

    // null means unlimited (or feature is gated, not count-limited)
    if (limit === null || limit === undefined) {
      return { allowed: true, limit: null, remaining: null };
    }

    const remaining = Math.max(0, limit - currentCount);
    return {
      allowed: currentCount < limit,
      limit,
      remaining,
      currentCount,
    };
  }, [subscriptionStatus.currentTier]);

  // Check if a feature is available for the user's current tier
  const canAccessFeature = useCallback((feature) => {
    // Users with active/trialing/lifetime status get tier-based access
    if (subscriptionStatus.isPro) {
      return canTierAccessFeature(subscriptionStatus.currentTier, feature);
    }
    // Free users only get features available to free tier
    return canTierAccessFeature('free', feature);
  }, [subscriptionStatus.isPro, subscriptionStatus.currentTier]);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    subscription,
    loading,
    subscriptionStatus,
    refreshSubscription,
    checkUsageLimit,
    canAccessFeature,
    FREE_LIMITS,
    PRO_ONLY_FEATURES,
  }), [subscription, loading, subscriptionStatus, refreshSubscription, checkUsageLimit, canAccessFeature]);

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export default SubscriptionContext;
